import React, { useState } from 'react';
import {
  Shield,
  Package,
  Wrench,
  QrCode,
  Search,
  CheckCircle2,
  Clock,
  Plus,
  ArrowRight,
  LogOut,
  AlertCircle,
  Truck,
  Check,
  UserCheck,
  MapPin,
  ClipboardList,
  Timer,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { MaintenanceTicket, Parcel } from '../types';
import { StaffAttendance } from './StaffAttendance';
import { SecurityPatrolScanner } from './SecurityPatrolScanner';
import { StaffLeaveManagement } from './StaffLeaveManagement';

export const StaffDashboard: React.FC = () => {
  const {
    currentUser,
    parcels,
    addParcel,
    markParcelPickedUp,
    maintenanceTickets,
    updateMaintenanceStatus,
    visitorPasses,
    checkInVisitor,
    checkOutVisitor,
    attendanceRecords,
    patrolCheckpoints,
    patrolLogs,
  } = useApp();

  const [activeTab, setActiveTab] = useState<'attendance' | 'patrol' | 'leave' | 'gate' | 'parcels' | 'maintenance'>('attendance');

  // Visitor Gate Scanner
  const [searchPassCode, setSearchPassCode] = useState('');
  const [scannerMsg, setScannerMsg] = useState<{ success: boolean; text: string } | null>(null);

  // New Parcel Inbound Form
  const [showParcelModal, setShowParcelModal] = useState(false);
  const [courierName, setCourierName] = useState('Flash Express');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [houseNumber, setHouseNumber] = useState('88/12');
  const [recipientName, setRecipientName] = useState('คุณอนุกูล พรหมดี');
  const [parcelNote, setParcelNote] = useState('กล่องพัสดุวางไว้ที่ชั้นวางป้อม รปภ.1');
  const [parcelImage, setParcelImage] = useState('https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=500&auto=format&fit=crop&q=80');

  // Maintenance Status Update
  const [updatingTicket, setUpdatingTicket] = useState<MaintenanceTicket | null>(null);
  const [newStatus, setNewStatus] = useState<MaintenanceTicket['status']>('in_progress');
  const [staffNote, setStaffNote] = useState('');
  const [assignedTechnician, setAssignedTechnician] = useState('ช่างสมศักดิ์');

  // Handle Pass Verification
  const handleVerifyPass = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchPassCode.trim()) return;

    const pass = visitorPasses.find(
      (p) => p.pass_code === searchPassCode.trim() || p.license_plate.includes(searchPassCode.trim())
    );

    if (!pass) {
      setScannerMsg({ success: false, text: `ไม่พบข้อมูลบัตรผ่านรหัส: ${searchPassCode}` });
      return;
    }

    if (pass.status === 'checked_in') {
      await checkOutVisitor(pass.pass_code);
      setScannerMsg({ success: true, text: `บันทึกเวลาออก (Check-out) สำเร็จ: ${pass.visitor_name} (ทะเบียน ${pass.license_plate})` });
    } else {
      await checkInVisitor(pass.pass_code);
      setScannerMsg({ success: true, text: `บันทึกเวลาเข้า (Check-in) สำเร็จ: ${pass.visitor_name} เข้าพบบ้าน ${pass.host_house_number}` });
    }
    setSearchPassCode('');
  };

  // Handle Add Parcel
  const handleAddParcel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trackingNumber) return;

    await addParcel({
      courier_name: courierName,
      tracking_number: trackingNumber,
      house_number: houseNumber,
      recipient_name: recipientName,
      note: parcelNote,
      image_url: parcelImage || undefined,
    });

    setShowParcelModal(false);
    setTrackingNumber('');
  };

  // Handle Update Maintenance
  const handleUpdateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!updatingTicket) return;

    await updateMaintenanceStatus(
      updatingTicket.id,
      newStatus,
      staffNote || updatingTicket.staff_notes,
      assignedTechnician
    );

    setUpdatingTicket(null);
    setStaffNote('');
  };

  const currentlyInVillage = visitorPasses.filter((v) => v.status === 'checked_in');
  const waitingParcelsCount = parcels.filter((p) => p.status === 'waiting').length;
  const pendingTicketsCount = maintenanceTickets.filter((t) => t.status !== 'completed').length;

  const todayStr = new Date().toISOString().split('T')[0];
  const myTodayAttendance = attendanceRecords.find(
    (r) => (r.user_id === currentUser?.id || r.staff_name === currentUser?.full_name) && r.date === todayStr
  );
  const todayPatrolLogsCount = patrolLogs.length;

  return (
    <div id="staff-dashboard-wrapper" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Staff Header Banner */}
      <div className="bg-gradient-to-r from-emerald-800 via-teal-800 to-slate-900 rounded-3xl p-6 text-white shadow-xl relative overflow-hidden border border-emerald-700/50">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 text-emerald-100 text-xs font-medium backdrop-blur-xs mb-2">
              <Shield className="w-3.5 h-3.5 text-emerald-300" />
              <span>ระบบปฏิบัติการเจ้าหน้าที่ • {currentUser?.department || 'ฝ่ายปฏิบัติการ & รปภ.'}</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold">
              ศูนย์ปฏิบัติการ: {currentUser?.full_name || 'เจ้าหน้าที่'}
            </h2>
            <p className="text-emerald-100 text-xs sm:text-sm mt-1 max-w-2xl">
              ลงเวลาเข้า-ออกงาน, ตรวจลาดตระเวนสแกน QR Code ประจำจุด, สแกนรถเข้า-ออก, รับพัสดุ และอัปเดตงานช่าง
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setActiveTab('attendance')}
              className="px-3.5 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-400/30 text-xs font-semibold rounded-xl transition-colors flex items-center gap-1.5"
            >
              <Timer className="w-4 h-4 text-emerald-300" />
              {myTodayAttendance?.clock_out_time ? 'ออกงานแล้ว' : myTodayAttendance ? 'กำลังปฏิบัติงาน' : 'ยังไม่ลงเวลา'}
            </button>

            <button
              onClick={() => setShowParcelModal(true)}
              className="px-4 py-2 bg-white text-emerald-950 hover:bg-emerald-50 text-xs font-bold rounded-xl shadow-sm transition-colors flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4 text-emerald-700" />
              ลงทะเบียนรับพัสดุ
            </button>
          </div>
        </div>
      </div>

      {/* Quick Status Stats (5 Cards) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {/* 1. Attendance KPI */}
        <div
          onClick={() => setActiveTab('attendance')}
          className={`p-3.5 sm:p-4 rounded-2xl border cursor-pointer transition-all ${
            activeTab === 'attendance'
              ? 'bg-emerald-50/90 border-emerald-400 ring-2 ring-emerald-500/30 shadow-sm'
              : 'bg-white border-slate-200 hover:shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600">ลงเวลาทำงาน</span>
            <div className="p-1.5 rounded-lg bg-emerald-100 text-emerald-800">
              <Clock className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-2 text-lg sm:text-xl font-bold text-slate-900 truncate">
            {myTodayAttendance ? myTodayAttendance.clock_in_time : 'ยังไม่เข้างาน'}
          </div>
          <p className="text-[10px] text-emerald-700 mt-0.5">
            {myTodayAttendance?.clock_out_time ? 'บันทึกออกงานแล้ว' : myTodayAttendance ? 'กำลังเข้าเวร' : 'แตะเพื่อลงเวลา'}
          </p>
        </div>

        {/* 2. QR Patrol KPI */}
        <div
          onClick={() => setActiveTab('patrol')}
          className={`p-3.5 sm:p-4 rounded-2xl border cursor-pointer transition-all ${
            activeTab === 'patrol'
              ? 'bg-teal-50/90 border-teal-400 ring-2 ring-teal-500/30 shadow-sm'
              : 'bg-white border-slate-200 hover:shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600">สแกนลาดตระเวน</span>
            <div className="p-1.5 rounded-lg bg-teal-100 text-teal-800">
              <QrCode className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-2 text-lg sm:text-xl font-bold text-slate-900">
            {todayPatrolLogsCount} <span className="text-xs font-normal text-slate-500">ครั้ง</span>
          </div>
          <p className="text-[10px] text-teal-700 mt-0.5">{patrolCheckpoints.length} จุดตรวจ 7 โซน</p>
        </div>

        {/* 3. Gate Visitor KPI */}
        <div
          onClick={() => setActiveTab('gate')}
          className={`p-3.5 sm:p-4 rounded-2xl border cursor-pointer transition-all ${
            activeTab === 'gate'
              ? 'bg-emerald-50/90 border-emerald-400 ring-2 ring-emerald-500/30 shadow-sm'
              : 'bg-white border-slate-200 hover:shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600">ผู้มาติดต่อในโครงการ</span>
            <div className="p-1.5 rounded-lg bg-emerald-100 text-emerald-800">
              <UserCheck className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-2 text-lg sm:text-xl font-bold text-slate-900">
            {currentlyInVillage.length} <span className="text-xs font-normal text-slate-500">คัน/คน</span>
          </div>
          <p className="text-[10px] text-slate-500 mt-0.5">รอ Check-out ป้อม 1</p>
        </div>

        {/* 4. Parcels KPI */}
        <div
          onClick={() => setActiveTab('parcels')}
          className={`p-3.5 sm:p-4 rounded-2xl border cursor-pointer transition-all ${
            activeTab === 'parcels'
              ? 'bg-amber-50/90 border-amber-400 ring-2 ring-amber-500/30 shadow-sm'
              : 'bg-white border-slate-200 hover:shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600">พัสดุรอส่งมอบ</span>
            <div className="p-1.5 rounded-lg bg-amber-100 text-amber-800">
              <Package className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-2 text-lg sm:text-xl font-bold text-slate-900">
            {waitingParcelsCount} <span className="text-xs font-normal text-slate-500">ชิ้น</span>
          </div>
          <p className="text-[10px] text-amber-700 mt-0.5">ชั้นวางป้อม รปภ.</p>
        </div>

        {/* 5. Maintenance KPI */}
        <div
          onClick={() => setActiveTab('maintenance')}
          className={`p-3.5 sm:p-4 rounded-2xl border cursor-pointer transition-all ${
            activeTab === 'maintenance'
              ? 'bg-blue-50/90 border-blue-400 ring-2 ring-blue-500/30 shadow-sm'
              : 'bg-white border-slate-200 hover:shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600">งานช่างรอซ่อม</span>
            <div className="p-1.5 rounded-lg bg-blue-100 text-blue-800">
              <Wrench className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-2 text-lg sm:text-xl font-bold text-slate-900">
            {pendingTicketsCount} <span className="text-xs font-normal text-slate-500">งาน</span>
          </div>
          <p className="text-[10px] text-blue-700 mt-0.5">รอดำเนินการซ่อม</p>
        </div>
      </div>

      {/* Main Tabs Navigation */}
      <div className="flex border-b border-slate-200 overflow-x-auto gap-2 text-xs sm:text-sm font-semibold pb-1">
        <button
          onClick={() => setActiveTab('attendance')}
          className={`pb-3 px-4 border-b-2 whitespace-nowrap transition-colors flex items-center gap-2 ${
            activeTab === 'attendance'
              ? 'border-emerald-600 text-emerald-800 font-bold'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <Clock className="w-4 h-4 text-emerald-600" />
          <span>ระบบลงเวลาเข้า-ออกงาน (Attendance)</span>
        </button>

        <button
          onClick={() => setActiveTab('patrol')}
          className={`pb-3 px-4 border-b-2 whitespace-nowrap transition-colors flex items-center gap-2 ${
            activeTab === 'patrol'
              ? 'border-emerald-600 text-emerald-800 font-bold'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <QrCode className="w-4 h-4 text-teal-600" />
          <span>ระบบลาดตระเวนสแกน QR Code (Security Patrol)</span>
        </button>

        <button
          onClick={() => setActiveTab('leave')}
          className={`pb-3 px-4 border-b-2 whitespace-nowrap transition-colors flex items-center gap-2 ${
            activeTab === 'leave'
              ? 'border-emerald-600 text-emerald-800 font-bold'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <Clock className="w-4 h-4 text-purple-600" />
          <span>ยื่นใบลา & สิทธิวันลา (Leave & Quota)</span>
        </button>

        <button
          onClick={() => setActiveTab('gate')}
          className={`pb-3 px-4 border-b-2 whitespace-nowrap transition-colors flex items-center gap-2 ${
            activeTab === 'gate'
              ? 'border-emerald-600 text-emerald-800 font-bold'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <Shield className="w-4 h-4 text-slate-600" />
          <span>ป้อม รปภ. / ตรวจสอบผู้มาติดต่อ</span>
        </button>

        <button
          onClick={() => setActiveTab('parcels')}
          className={`pb-3 px-4 border-b-2 whitespace-nowrap transition-colors flex items-center gap-2 ${
            activeTab === 'parcels'
              ? 'border-emerald-600 text-emerald-800 font-bold'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <Package className="w-4 h-4 text-amber-600" />
          <span>ระบบรับ-จ่ายพัสดุ ({waitingParcelsCount})</span>
        </button>

        <button
          onClick={() => setActiveTab('maintenance')}
          className={`pb-3 px-4 border-b-2 whitespace-nowrap transition-colors flex items-center gap-2 ${
            activeTab === 'maintenance'
              ? 'border-emerald-600 text-emerald-800 font-bold'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <Wrench className="w-4 h-4 text-blue-600" />
          <span>กระดานงานช่าง ({pendingTicketsCount})</span>
        </button>
      </div>

      {/* 1. STAFF ATTENDANCE TAB */}
      {activeTab === 'attendance' && <StaffAttendance />}

      {/* 2. SECURITY QR PATROL SCANNER TAB */}
      {activeTab === 'patrol' && <SecurityPatrolScanner />}

      {/* 3. STAFF LEAVE MANAGEMENT */}
      {activeTab === 'leave' && <StaffLeaveManagement isStaffSelfService={true} />}

      {/* 4. GATE SECURITY SCANNER */}
      {activeTab === 'gate' && (
        <div className="space-y-6">
          {/* Scanner Input Box */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
              <QrCode className="w-5 h-5 text-emerald-600" />
              สแกนหรือกรอกรหัสบัตรผ่าน (Pass Code / ทะเบียนรถ)
            </h3>

            <form onSubmit={handleVerifyPass} className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="กรอกรหัสผ่าน 6 หลัก เช่น 884920 หรือ ทะเบียนรถ 3กข-8921"
                  value={searchPassCode}
                  onChange={(e) => setSearchPassCode(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 font-mono outline-none"
                />
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
              </div>

              <button
                type="submit"
                className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl shadow-sm transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle2 className="w-4 h-4" />
                ตรวจสอบ / บันทึก เข้า-ออก
              </button>
            </form>

            {scannerMsg && (
              <div
                className={`p-3.5 rounded-xl text-xs flex items-center gap-2.5 ${
                  scannerMsg.success
                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                    : 'bg-rose-50 border border-rose-200 text-rose-800'
                }`}
              >
                {scannerMsg.success ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                )}
                <span className="font-semibold">{scannerMsg.text}</span>
              </div>
            )}
          </div>

          {/* Table of active visitors in village */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
            <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h4 className="font-semibold text-slate-800 text-sm">
                รายชื่อผู้มาติดต่อที่ยังอยู่ในโครงการ ({currentlyInVillage.length} รายการ)
              </h4>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50/75 text-slate-500 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="p-3.5">รหัสผ่าน / ทะเบียน</th>
                    <th className="p-3.5">ผู้มาติดต่อ</th>
                    <th className="p-3.5">บ้านที่เข้าพบ</th>
                    <th className="p-3.5">เวลา Check-in</th>
                    <th className="p-3.5">วัตถุประสงค์</th>
                    <th className="p-3.5 text-right">ดำเนินการ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentlyInVillage.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-400">
                        ไม่มีผู้มาติดต่อที่ค้างอยู่ในโครงการขณะนี้
                      </td>
                    </tr>
                  ) : (
                    currentlyInVillage.map((v) => (
                      <tr key={v.id} className="hover:bg-slate-50/50">
                        <td className="p-3.5">
                          <span className="font-mono font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded mr-2">
                            {v.pass_code}
                          </span>
                          <span className="font-bold text-slate-800">{v.license_plate}</span>
                        </td>
                        <td className="p-3.5 font-medium text-slate-800">{v.visitor_name}</td>
                        <td className="p-3.5 font-medium text-slate-700">บ้านเลขที่ {v.host_house_number}</td>
                        <td className="p-3.5 font-mono text-slate-500">{v.check_in_time || '-'}</td>
                        <td className="p-3.5 text-slate-600">{v.purpose}</td>
                        <td className="p-3.5 text-right">
                          <button
                            onClick={() => checkOutVisitor(v.pass_code)}
                            className="px-3 py-1 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-medium transition-colors"
                          >
                            บันทึกออก (Check-out)
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 4. PARCELS MANAGEMENT */}
      {activeTab === 'parcels' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h3 className="font-bold text-slate-800 text-base">
              รายการพัสดุและไปรษณีย์ทั้งหมดในระบบ ({parcels.length} ชิ้น)
            </h3>

            <button
              onClick={() => setShowParcelModal(true)}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl shadow-sm transition-colors flex items-center justify-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              รับพัสดุเข้าใหม่
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {parcels.map((parcel) => (
              <div
                key={parcel.id}
                className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 hover:shadow-md transition-shadow flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <span className="px-2.5 py-1 bg-amber-100 text-amber-800 rounded-lg text-[11px] font-bold">
                      {parcel.courier_name}
                    </span>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                        parcel.status === 'waiting'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-emerald-100 text-emerald-800'
                      }`}
                    >
                      {parcel.status === 'waiting' ? 'รอลูกค้ารับ' : 'รับแล้ว'}
                    </span>
                  </div>

                  <div>
                    <h4 className="font-bold text-slate-800 text-sm">
                      บ้านเลขที่ {parcel.house_number} ({parcel.recipient_name})
                    </h4>
                    <p className="text-xs font-mono text-slate-500 mt-0.5">
                      Tracking: {parcel.tracking_number}
                    </p>
                  </div>

                  <div className="text-xs text-slate-600 bg-slate-50 p-2.5 rounded-xl space-y-1">
                    <p><b>ตำแหน่ง:</b> {parcel.note || 'ชั้นวางพัสดุป้อม 1'}</p>
                    <p className="text-[11px] text-slate-400">
                      รับเมื่อ: {new Date(parcel.received_at).toLocaleDateString('th-TH', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    {parcel.picked_up_at && (
                      <p className="text-[11px] text-emerald-600">
                        ส่งมอบเมื่อ: {new Date(parcel.picked_up_at).toLocaleDateString('th-TH', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                </div>

                {parcel.status === 'waiting' && (
                  <button
                    onClick={() => markParcelPickedUp(parcel.id)}
                    className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Check className="w-4 h-4" />
                    ยืนยันส่งมอบให้ลูกบ้าน
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 5. MAINTENANCE TICKETS */}
      {activeTab === 'maintenance' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-800 text-base">
              รายการแจ้งซ่อมบำรุงในโครงการ ({maintenanceTickets.length} รายการ)
            </h3>
          </div>

          <div className="space-y-3">
            {maintenanceTickets.map((ticket) => (
              <div
                key={ticket.id}
                className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:shadow-xs transition-all"
              >
                <div className="space-y-1 max-w-xl">
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 bg-slate-100 text-slate-700 rounded-md font-semibold text-[11px]">
                      {ticket.category === 'plumbing' ? 'ประปา' : ticket.category === 'electrical' ? 'ไฟฟ้า' : ticket.category === 'gardening' ? 'สวน/ภูมิทัศน์' : 'ทั่วไป'}
                    </span>
                    <span className="text-xs font-medium text-slate-500">
                      บ้านเลขที่ {ticket.house_number} ({ticket.resident_name})
                    </span>
                  </div>
                  <h4 className="font-bold text-slate-900 text-sm">{ticket.title}</h4>
                  <p className="text-xs text-slate-600">{ticket.description}</p>
                  {ticket.staff_notes && (
                    <div className="text-[11px] bg-slate-50 p-2 rounded-lg text-slate-700 mt-2">
                      <span className="font-semibold text-emerald-800">บันทึกช่าง:</span> {ticket.staff_notes}
                      {ticket.assigned_technician && ` (โดย: ${ticket.assigned_technician})`}
                    </div>
                  )}
                </div>

                <div className="flex sm:flex-col items-center sm:items-end justify-between gap-2 shrink-0">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold ${
                      ticket.status === 'completed'
                        ? 'bg-emerald-100 text-emerald-800'
                        : ticket.status === 'in_progress'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {ticket.status === 'completed' ? 'ซ่อมเสร็จแล้ว' : ticket.status === 'in_progress' ? 'กำลังดำเนินการ' : 'รอดำเนินการ'}
                  </span>

                  <button
                    onClick={() => {
                      setUpdatingTicket(ticket);
                      setNewStatus(ticket.status);
                      setStaffNote(ticket.staff_notes || '');
                      setAssignedTechnician(ticket.assigned_technician || 'ช่างสมศักดิ์');
                    }}
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-semibold flex items-center gap-1 transition-colors"
                  >
                    <Wrench className="w-3.5 h-3.5" />
                    อัปเดตสถานะงาน
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MODAL: ADD PARCEL */}
      {showParcelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 bg-emerald-700 text-white flex items-center justify-between">
              <h3 className="font-semibold text-base flex items-center gap-2">
                <Package className="w-5 h-5" />
                ลงทะเบียนรับพัสดุเข้าป้อม รปภ.
              </h3>
              <button
                onClick={() => setShowParcelModal(false)}
                className="text-white/80 hover:text-white"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddParcel} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">บริษัทขนส่ง</label>
                <select
                  value={courierName}
                  onChange={(e) => setCourierName(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-emerald-500 outline-none"
                >
                  <option value="Flash Express">Flash Express</option>
                  <option value="Kerry Express">Kerry Express</option>
                  <option value="Shopee Express (SPX)">Shopee Express (SPX)</option>
                  <option value="ไปรษณีย์ไทย (EMS)">ไปรษณีย์ไทย (EMS)</option>
                  <option value="J&T Express">J&T Express</option>
                  <option value="DHL / อื่นๆ">DHL / อื่นๆ</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">บ้านเลขที่ผู้รับ</label>
                <input
                  type="text"
                  placeholder="เช่น 88/12"
                  value={houseNumber}
                  onChange={(e) => setHouseNumber(e.target.value)}
                  required
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">ชื่อผู้รับบนจ่าหน้า</label>
                <input
                  type="text"
                  placeholder="เช่น คุณอนุกูล พรหมดี"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  required
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">หมายเลขพัสดุ (Tracking Number)</label>
                <input
                  type="text"
                  placeholder="เช่น TH1234567890"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  required
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">ตำแหน่งจัดเก็บ / หมายเหตุ</label>
                <input
                  type="text"
                  placeholder="เช่น วางชั้นวาง A2 ป้อม 1"
                  value={parcelNote}
                  onChange={(e) => setParcelNote(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none"
                />
              </div>

              <div className="pt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowParcelModal(false)}
                  className="px-4 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-xl"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl shadow-sm transition-colors"
                >
                  บันทึกรับพัสดุ
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: UPDATE MAINTENANCE TICKET */}
      {updatingTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between">
              <h3 className="font-semibold text-base flex items-center gap-2">
                <Wrench className="w-5 h-5 text-emerald-400" />
                อัปเดตงานช่าง: {updatingTicket.title}
              </h3>
              <button
                onClick={() => setUpdatingTicket(null)}
                className="text-white/80 hover:text-white"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateTicket} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">สถานะงาน</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as MaintenanceTicket['status'])}
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none font-semibold"
                >
                  <option value="pending">⏳ รอดำเนินการ (Pending)</option>
                  <option value="in_progress">🔧 กำลังดำเนินการซ่อม (In Progress)</option>
                  <option value="completed">✅ ซ่อมเสร็จสิ้นแล้ว (Completed)</option>
                  <option value="cancelled">❌ ยกเลิกงาน (Cancelled)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">ช่างผู้รับผิดชอบ</label>
                <input
                  type="text"
                  value={assignedTechnician}
                  onChange={(e) => setAssignedTechnician(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">บันทึกความคืบหน้าของช่าง</label>
                <textarea
                  rows={3}
                  value={staffNote}
                  onChange={(e) => setStaffNote(e.target.value)}
                  placeholder="ระบุสิ่งที่ดำเนินการ เช่น เปลี่ยนหลอดไฟ LED เรียบร้อย, ซ่อมท่อและทดสอบแรงดันน้ำผ่าน..."
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div className="pt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setUpdatingTicket(null)}
                  className="px-4 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-xl"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-xl shadow-sm transition-colors"
                >
                  บันทึกการเปลี่ยนแปลง
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
